import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from '../utils/auth';
import { supabase } from '../utils/supabaseClient';
import { createRecipe, updateRecipe, deleteRecipe as deleteRecipeFromDB, getPublicRecipes } from '../utils/recipeService';
import { createProgram, updateProgram, deleteProgram as deleteProgramFromDB, getPublicPrograms } from '../utils/workoutService';
import { createResource, updateResource, deleteResource as deleteResourceFromDB, getPublicResources } from '../services/resourceService';
import { createWorkout, updateWorkout, deleteWorkout as deleteWorkoutFromDB, getPublicWorkouts } from '../services/workoutLibraryService';
import { uploadImageWithCompression, deleteImage } from '../utils/imageUpload';
import SimpleProgramBuilder from '../components/SimpleProgramBuilder';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('recipes');
  const [recipes, setRecipes] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showArchivedPrograms, setShowArchivedPrograms] = useState(false);
  const [resources, setResources] = useState([]);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showExerciseLibraryModal, setShowExerciseLibraryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [storageUsage, setStorageUsage] = useState(0);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [useLibraryExercise, setUseLibraryExercise] = useState(false);
  const [programViewMode, setProgramViewMode] = useState('list'); // 'list' or 'calendar'
  const [exerciseSearchQuery, setExerciseSearchQuery] = useState('');
  const [showExerciseSuggestions, setShowExerciseSuggestions] = useState(false);
  const [lastAddedExerciseId, setLastAddedExerciseId] = useState(null);
  const [programBuilderStep, setProgramBuilderStep] = useState(1); // 1 = basics, 2 = build workouts
  const [selectedWeekForBuilder, setSelectedWeekForBuilder] = useState(0);
  const [selectedDayForBuilder, setSelectedDayForBuilder] = useState(null);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const navigate = useNavigate();
  const { isAdminUser, loading: authLoading } = useAuth();

  // Recipe form state
  const [recipeForm, setRecipeForm] = useState({
    title: '',
    image: '',
    prepTime: '',
    servings: 1,
    difficulty: 'Easy',
    mealType: 'dinner',
    dietary: '',
    nutritionHighlights: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    ingredients: '',
    instructions: ''
  });

  // Workout form state
  const [workoutForm, setWorkoutForm] = useState({
    name: '',
    description: '',
    duration: '',
    difficulty: 'Beginner',
    equipment: '',
    targetMuscles: '',
    exercises: '',
    image: ''
  });

  // Program form state
  const [programForm, setProgramForm] = useState({
    title: '',
    description: '',
    duration: '4 weeks',
    frequency: '4 days/week',
    level: 'Intermediate',
    equipment: '',
    goals: '',
    color: 'from-blue-100 to-cyan-100',
    iconBg: 'bg-blue-500',
    icon: '💪',
    weeks: [
      { week: 1, title: '', workouts: [] },
      { week: 2, title: '', workouts: [] },
      { week: 3, title: '', workouts: [] },
      { week: 4, title: '', workouts: [] }
    ]
  });
  const [currentWeek, setCurrentWeek] = useState(0);
  const [currentWorkout, setCurrentWorkout] = useState(null);
  const [workoutFormInProgram, setWorkoutFormInProgram] = useState({
    id: '',
    title: '',
    day: '',
    duration: '',
    difficulty: 'Beginner',
    equipment: [],
    exercises: []
  });
  const [exerciseForm, setExerciseForm] = useState({
    id: '',
    name: '',
    supersetGroup: '', // e.g., 'A1', 'A2', 'B1', 'B2' or empty for standalone
    sets: [
      { setNumber: 1, reps: '10-12', type: 'normal', isTimed: false }
    ],
    tempo: '', // e.g., '3-1-1-0' (eccentric-pause-concentric-pause)
    restTime: '60s',
    instructions: '',
    muscleGroups: [],
    needsWeight: false,
    videoUrl: ''
  });

  // Resource form state
  const [resourceForm, setResourceForm] = useState({
    title: '',
    category: 'lifestyle',
    type: 'guide',
    description: '',
    content: '',
    readTime: '',
    downloadable: false,
    featured: false,
    tags: '',
    link: '',
    image: '',
    author: ''
  });

  useEffect(() => {
    if (!authLoading && !isAdminUser) {
      navigate('/admin-login');
      return;
    }
    if (isAdminUser) {
      loadData();
      calculateStorageUsage();
    }
  }, [isAdminUser, authLoading, navigate]);

  // Track the content to initialize editor with (only updates when modal opens or editing changes)
  const [editorInitialContent, setEditorInitialContent] = useState('');
  
  // Set initial content when modal opens
  useEffect(() => {
    if (showResourceModal) {
      setEditorInitialContent(resourceForm.content || '');
    }
  }, [showResourceModal, editingItem]);

  // Initialize resource editor content when modal opens
  useEffect(() => {
    if (showResourceModal && editorInitialContent !== undefined) {
      const timeoutId = setTimeout(() => {
        try {
          const editor = document.getElementById('resource-content-editor');
          if (editor) {
            editor.innerHTML = editorInitialContent;
          }
        } catch (err) {
          console.error('Error initializing editor:', err);
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [showResourceModal, editorInitialContent]);

  const calculateStorageUsage = () => {
    try {
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length;
        }
      }
      // Convert to MB and calculate percentage (localStorage limit is usually 5-10MB)
      const sizeInMB = totalSize / (1024 * 1024);
      const usagePercent = (sizeInMB / 5) * 100; // Assuming 5MB limit
      setStorageUsage(Math.min(usagePercent, 100));
    } catch (e) {
      console.warn('Failed to calculate storage usage');
    }
  };

  const clearAllStorage = () => {
    if (window.confirm('This will clear localStorage data (workouts only). Recipes, Programs, and Resources are in Supabase and won\'t be affected. Continue?')) {
      try {
        localStorage.removeItem('adminWorkouts');
        localStorage.removeItem('exerciseLibrary');
        setWorkouts([]);
        calculateStorageUsage();
        alert('localStorage cleared successfully! (Recipes, Programs, and Resources in Supabase are safe)');
      } catch (e) {
        alert('Failed to clear storage');
      }
    }
  };

  const loadPrograms = async () => {
    try {
      const result = await getPublicPrograms();
      if (result.success) {
        // Get raw data from Supabase
        const { data: rawPrograms } = await supabase
          .from('workout_programs')
          .select('*')
          .eq('is_public', true)
          .order('created_at', { ascending: false });
        
        // Store raw programs with ALL fields for editing
        const adminPrograms = (rawPrograms || []).map(p => ({
          // Keep ALL original fields for editing
          ...p,
          // Add display fields
          difficulty: p.level?.charAt(0).toUpperCase() + p.level?.slice(1) || 'Beginner',
          image: p.icon || '',
          totalWorkouts: p.total_workouts || 0,
          completedWorkouts: 0
        }));
        setPrograms(adminPrograms);
        console.log('Loaded programs from Supabase:', adminPrograms.length);
      }
    } catch (error) {
      console.error('Error loading programs:', error);
    }
  };

  const loadWorkouts = async () => {
    try {
      const result = await getPublicWorkouts();
      if (result.success) {
        setWorkouts(result.data || []);
      } else {
        console.error('Failed to load workouts:', result.error);
      }
    } catch (error) {
      console.error('Error loading workouts:', error);
    }
  };

  const loadData = () => {
    // Load recipes from Supabase
    loadRecipes();
    
    // Load programs from Supabase
    loadPrograms();
    
    // Load resources from Supabase
    loadResources();
    
    // Load workouts from Supabase
    loadWorkouts();

    // Load exercise library from localStorage
    try {
      const savedExercises = localStorage.getItem('exerciseLibrary');
      if (savedExercises) {
        const parsed = JSON.parse(savedExercises);
        // Validate each exercise to ensure muscleGroups is always an array
        const validated = parsed.map(ex => ({
          ...ex,
          muscleGroups: Array.isArray(ex.muscleGroups) 
            ? ex.muscleGroups 
            : (typeof ex.muscleGroups === 'string' && ex.muscleGroups 
              ? ex.muscleGroups.split(',').map(m => m.trim()).filter(m => m) 
              : [])
        }));
        setExerciseLibrary(validated);
        localStorage.setItem('exerciseLibrary', JSON.stringify(validated));
      } else {
        setExerciseLibrary([]);
      }
    } catch (e) {
      console.warn('Failed to load exercise library');
    }
  };

  // Old loadData code removed - keeping this comment for reference
  const OLD_loadDataRecipePart = () => {
    // This was the old localStorage-based recipe loading
    // Now handled by loadRecipes() which uses Supabase
    try {
      const savedRecipes = localStorage.getItem('adminRecipes');
      let allRecipes = [];
      
      // Essential default recipe that should always be available
      const defaultRecipe = {
        id: 1,
        title: 'Go To Pancakes',
        image: '🥞',
        prepTime: '15 mins',
        servings: 4,
        difficulty: 'Easy',
        rating: 4.8,
        reviews: 124,
        mealType: 'breakfast',
        dietary: 'gluten-free, dairy-free',
        nutritionHighlights: 'High Protein, Low Carb, Fiber Rich',
        ingredients: 'Coconut flour | 1/2 cup | Baking\nEggs | 4 large | Dairy & Eggs\nCoconut milk | 1/4 cup | Pantry\nVanilla extract | 1 tsp | Pantry\nBaking powder | 1/2 tsp | Baking\nSalt | 1/4 tsp | Pantry',
        instructions: 'Whisk together coconut flour, baking powder, and salt\nIn another bowl, beat eggs and add coconut milk and vanilla\nCombine wet and dry ingredients until smooth\nCook on medium heat for 2-3 minutes per side'
      };
      
      if (savedRecipes) {
        const parsedRecipes = JSON.parse(savedRecipes);
        // Check if Go To Pancakes exists, if not add it
        const hasDefaultRecipe = parsedRecipes.some(r => r.title === 'Go To Pancakes');
        if (!hasDefaultRecipe) {
          allRecipes = [defaultRecipe, ...parsedRecipes];
        } else {
          allRecipes = parsedRecipes;
        }
      } else {
        // First time - start with default recipe
        allRecipes = [defaultRecipe];
      }
      
      setRecipes(allRecipes);
      
      // Force save to ensure consistency
      localStorage.setItem('adminRecipes', JSON.stringify(allRecipes));
      
      // Sync all recipes to publicRecipes for members
      const publicData = {
        recipes: allRecipes.map(r => ({
          ...r,
          dietary: typeof r.dietary === 'string' ? r.dietary.split(',').map(d => d.trim()).filter(d => d) : r.dietary,
          nutritionHighlights: typeof r.nutritionHighlights === 'string' ? r.nutritionHighlights.split(',').map(n => n.trim()).filter(n => n) : r.nutritionHighlights,
          ingredients: typeof r.ingredients === 'string' ? r.ingredients.split('\n').map(line => {
            const parts = line.split('|').map(p => p.trim());
            return {
              name: parts[0] || '',
              amount: parts[1] || '',
              category: parts[2] || 'Other'
            };
          }).filter(i => i.name) : r.ingredients,
          instructions: typeof r.instructions === 'string' ? r.instructions.split('\n').filter(i => i.trim()) : r.instructions
        }))
      };
      localStorage.setItem('publicRecipes', JSON.stringify(publicData));
      
      // Trigger update for member pages
      window.dispatchEvent(new CustomEvent('recipesUpdated', { detail: publicData }));
      
      console.log('Loaded admin recipes:', allRecipes);
    } catch (e) {
      console.warn('Failed to load recipes');
    }

    // Load workouts from localStorage (legacy individual workouts)
    try {
      const savedWorkouts = localStorage.getItem('adminWorkouts');
      if (savedWorkouts) {
        setWorkouts(JSON.parse(savedWorkouts));
      }
    } catch (e) {
      console.warn('Failed to load workouts');
    }

    // Load programs from localStorage
    try {
      const savedPrograms = localStorage.getItem('adminPrograms');
      if (savedPrograms) {
        setPrograms(JSON.parse(savedPrograms));
      }
    } catch (e) {
      console.warn('Failed to load programs');
    }

    // Load resources from localStorage
    try {
      const savedResources = localStorage.getItem('adminResources');
      if (savedResources) {
        setResources(JSON.parse(savedResources));
      } else {
        setResources([]);
      }
    } catch (e) {
      console.warn('Failed to load resources');
    }
  };

  const saveRecipes = async (newRecipes) => {
    // This function is now deprecated - recipes are saved individually via handleSaveRecipe
    // Keeping for backwards compatibility but it won't do anything
    console.warn('saveRecipes is deprecated - use handleSaveRecipe instead');
  };

  const handleSaveRecipe = async () => {
    if (!recipeForm.title || !recipeForm.ingredients || !recipeForm.instructions) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      console.log('Saving recipe form:', recipeForm); // Debug log
      
      // Parse dietary/allergen_free
      const allergenFree = typeof recipeForm.dietary === 'string' 
        ? recipeForm.dietary.split(',').map(d => d.trim()).filter(d => d)
        : (Array.isArray(recipeForm.dietary) ? recipeForm.dietary : []);
      
      // Parse ingredients
      const ingredients = typeof recipeForm.ingredients === 'string'
        ? recipeForm.ingredients.split('\n').map(line => {
            const parts = line.split('|').map(p => p.trim());
            return {
              name: parts[0] || '',
              amount: parts[1] || '',
              unit: parts[2] || ''
            };
          }).filter(i => i.name)
        : (Array.isArray(recipeForm.ingredients) ? recipeForm.ingredients : []);
      
      // Parse instructions
      const instructions = typeof recipeForm.instructions === 'string'
        ? recipeForm.instructions.split('\n').filter(i => i.trim())
        : (Array.isArray(recipeForm.instructions) ? recipeForm.instructions : []);
      
      // Parse tags
      const tags = typeof recipeForm.nutritionHighlights === 'string'
        ? recipeForm.nutritionHighlights.split(',').map(n => n.trim()).filter(n => n)
        : (Array.isArray(recipeForm.nutritionHighlights) ? recipeForm.nutritionHighlights : []);
      
      // Convert form data to Supabase format
      // Preserve existing image if no new one is provided
      console.log('🖼️ recipeForm.image:', recipeForm.image);
      console.log('🖼️ editingItem?.image:', editingItem?.image);
      
      const imageUrl = recipeForm.image || (editingItem?.image) || null;
      console.log('🖼️ Final imageUrl to save:', imageUrl);
      
      // Build nutrition object
      const nutrition = {};
      if (recipeForm.calories) nutrition.calories = parseInt(recipeForm.calories);
      if (recipeForm.protein) nutrition.protein = parseFloat(recipeForm.protein);
      if (recipeForm.carbs) nutrition.carbs = parseFloat(recipeForm.carbs);
      if (recipeForm.fats) nutrition.fats = parseFloat(recipeForm.fats);

      const recipeData = {
        title: recipeForm.title,
        description: recipeForm.nutritionHighlights || null,
        image_url: imageUrl,
        prep_time: recipeForm.prepTime || null,
        cook_time: null,
        servings: parseInt(recipeForm.servings) || 1,
        difficulty: recipeForm.difficulty?.toLowerCase() || 'easy',
        meal_type: recipeForm.mealType || 'dinner',
        allergen_free: allergenFree,
        ingredients: ingredients,
        instructions: instructions,
        tags: tags,
        nutrition: Object.keys(nutrition).length > 0 ? nutrition : null,
        is_public: true
      };
      
      console.log('💾 Converted recipe data for Supabase:', recipeData);

      let result;
      if (editingItem) {
        // Update existing recipe - preserve existing image if no new one provided
        result = await updateRecipe(editingItem.id, recipeData);
      } else {
        // Create new recipe
        result = await createRecipe(recipeData);
      }

      if (result.success) {
        alert(editingItem ? 'Recipe updated successfully!' : 'Recipe created successfully!');
        setShowRecipeModal(false);
        resetRecipeForm();
        // Reload recipes
        loadRecipes();
        // Dispatch event for member pages
        window.dispatchEvent(new CustomEvent('recipesUpdated'));
      } else {
        alert('Failed to save recipe: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Error saving recipe. Please try again.');
    }
  };

  const loadRecipes = async () => {
    try {
      // Get raw data directly from Supabase with ALL fields
      const { data: rawRecipes } = await supabase
        .from('recipes')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });
      
      if (rawRecipes) {
        // Keep ALL fields, just convert image placeholder
        const adminRecipes = rawRecipes.map(r => ({
          ...r, // Keep ALL original fields including calories, protein, carbs, fat
          image: r.image === '🍽️' ? '' : r.image // Convert emoji placeholder back to empty string
        }));
        setRecipes(adminRecipes);
        console.log('✅ Loaded recipes from Supabase:', adminRecipes.length);
      }
    } catch (error) {
      console.error('Error loading recipes:', error);
    }
  };

  const loadResources = async () => {
    try {
      const result = await getPublicResources();
      if (result.success && result.data) {
        setResources(result.data);
        console.log('✅ Loaded resources from Supabase:', result.data.length);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  };

  // saveWorkouts removed - now using Supabase via handleWorkoutSubmit

  const savePrograms = (newPrograms) => {
    setPrograms(newPrograms);
    localStorage.setItem('adminPrograms', JSON.stringify(newPrograms));
    // Sync to public programs for members
    localStorage.setItem('publicPrograms', JSON.stringify({ programs: newPrograms }));
    window.dispatchEvent(new CustomEvent('programsUpdated', { detail: { programs: newPrograms } }));
    calculateStorageUsage();
  };

  // saveResources removed - now using Supabase via handleResourceSubmit

  const handleRecipeSubmit = (e) => {
    e.preventDefault();
    handleSaveRecipe();
  };

  const handleWorkoutSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Parse exercises from text format
      const exercisesArray = workoutForm.exercises.split('\n').map(line => {
        const parts = line.split('|').map(p => p.trim());
        return {
          name: parts[0] || '',
          sets: parts[1] || '3',
          reps: parts[2] || '10',
          rest: parts[3] || '60s'
        };
      }).filter(e => e.name);

      // Prepare workout data for Supabase
      const workoutData = {
        title: workoutForm.title,
        description: workoutForm.description || null,
        duration: workoutForm.duration || null,
        difficulty: workoutForm.difficulty || null,
        equipment: workoutForm.equipment ? workoutForm.equipment.split(',').map(e => e.trim()).filter(Boolean) : [],
        exercises: exercisesArray,
        program_id: workoutForm.program_id || null,
        week_number: workoutForm.week_number || null,
        day_number: workoutForm.day_number || null,
        is_public: true
      };

      let result;
      if (editingItem) {
        // Update existing workout
        result = await updateWorkout(editingItem.id, workoutData);
      } else {
        // Create new workout
        result = await createWorkout(workoutData);
      }

      if (result.success) {
        // Reload workouts from Supabase
        await loadWorkouts();
        resetWorkoutForm();
        setShowWorkoutModal(false);
        alert(editingItem ? 'Workout updated successfully!' : 'Workout created successfully!');
      } else {
        alert('Failed to save workout: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving workout:', error);
      alert('Failed to save workout');
    }
  };

  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    
    if (!resourceForm.title || !resourceForm.description) {
      alert('Please fill in title and description');
      return;
    }

    // Grab the latest content from the editor before submitting
    const editorContent = document.getElementById('resource-content-editor')?.innerHTML || resourceForm.content || '';

    try {
      // Parse tags
      const tags = typeof resourceForm.tags === 'string'
        ? resourceForm.tags.split(',').map(t => t.trim()).filter(t => t)
        : (Array.isArray(resourceForm.tags) ? resourceForm.tags : []);

      // Prepare resource data for Supabase
      const resourceData = {
        title: resourceForm.title,
        description: resourceForm.description,
        category: resourceForm.category,
        type: resourceForm.type,
        image_url: resourceForm.image || null,
        content: editorContent || null,
        author: resourceForm.author || null,
        tags: tags,
        read_time: resourceForm.readTime || null,
        featured: resourceForm.featured || false,
        link: resourceForm.link || null,
        is_public: true
      };

      let result;
      if (editingItem) {
        // Update existing resource
        result = await updateResource(editingItem.id, resourceData);
      } else {
        // Create new resource
        result = await createResource(resourceData);
      }

      if (result.success) {
        alert(editingItem ? 'Resource updated successfully!' : 'Resource created successfully!');
        setShowResourceModal(false);
        resetResourceForm();
        // Reload resources
        loadResources();
        // Dispatch event for member pages
        window.dispatchEvent(new CustomEvent('resourcesUpdated'));
      } else {
        alert('Failed to save resource: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error saving resource. Please try again.');
    }
  };

  const resetRecipeForm = () => {
    setRecipeForm({
      title: '', image: '', prepTime: '', servings: 1, difficulty: 'Easy',
      mealType: 'dinner', dietary: '', nutritionHighlights: '', 
      calories: '', protein: '', carbs: '', fats: '',
      ingredients: '', instructions: ''
    });
    setEditingItem(null);
  };

  const resetWorkoutForm = () => {
    setWorkoutForm({
      name: '', description: '', duration: '', difficulty: 'Beginner',
      equipment: '', targetMuscles: '', exercises: '', image: ''
    });
    setEditingItem(null);
  };

  const resetProgramForm = () => {
    setProgramForm({
      title: '',
      description: '',
      duration: '4 weeks',
      frequency: '4 days/week',
      level: 'Intermediate',
      equipment: '',
      goals: '',
      color: 'from-blue-100 to-cyan-100',
      iconBg: 'bg-blue-500',
      icon: '💪',
      weeks: [
        { week: 1, title: '', workouts: [] },
        { week: 2, title: '', workouts: [] },
        { week: 3, title: '', workouts: [] },
        { week: 4, title: '', workouts: [] }
      ]
    });
    setCurrentWeek(0);
    setCurrentWorkout(null);
    setEditingItem(null);
  };

  const resetResourceForm = () => {
    setResourceForm({
      title: '', category: 'lifestyle', type: 'guide', description: '', content: '', readTime: '',
      downloadable: false, featured: false, tags: '', link: '', image: '', author: ''
    });
    setEditingItem(null);
  };

  const editRecipe = (recipe) => {
    console.log('🔍 Editing recipe:', recipe);
    console.log('🖼️ Recipe image value:', recipe.image);
    
    // Convert dietary (allergen_free array) to comma-separated string
    // Check both allergen_free (DB) and dietary (form) fields
    const allergenFreeArray = recipe.allergen_free || recipe.dietary || [];
    const dietaryString = Array.isArray(allergenFreeArray) 
      ? allergenFreeArray.join(', ') 
      : (typeof allergenFreeArray === 'string' ? allergenFreeArray : '');
    
    // Convert nutritionHighlights (tags array) to comma-separated string
    const tagsArray = recipe.tags || recipe.nutritionHighlights || [];
    const nutritionString = Array.isArray(tagsArray) 
      ? tagsArray.join(', ') 
      : (typeof tagsArray === 'string' ? tagsArray : '');
    
    // Convert ingredients array to multi-line string format
    const ingredientsString = Array.isArray(recipe.ingredients) 
      ? recipe.ingredients.map(i => {
          const category = i.unit || i.category || '';
          return `${i.name || ''} | ${i.amount || ''} | ${category}`;
        }).join('\n') 
      : (typeof recipe.ingredients === 'string' ? recipe.ingredients : '');
    
    // Convert instructions array to multi-line string
    const instructionsString = Array.isArray(recipe.instructions) 
      ? recipe.instructions.join('\n') 
      : (typeof recipe.instructions === 'string' ? recipe.instructions : '');
    
    const formData = {
      title: recipe.title || '',
      image: recipe.image || recipe.image_url || '',
      prepTime: recipe.prep_time || recipe.prepTime || '',
      servings: recipe.servings || 1,
      difficulty: recipe.difficulty || 'Easy',
      mealType: recipe.meal_type || recipe.mealType || 'dinner',
      dietary: dietaryString,
      nutritionHighlights: nutritionString,
      calories: recipe.nutrition?.calories || '',
      protein: recipe.nutrition?.protein || '',
      carbs: recipe.nutrition?.carbs || '',
      fats: recipe.nutrition?.fats || '',
      ingredients: ingredientsString,
      instructions: instructionsString
    };
    
    console.log('📝 Setting recipe form data:', formData);
    setRecipeForm(formData);
    
    console.log('💾 Setting editingItem:', recipe);
    setEditingItem(recipe);
    setShowRecipeModal(true);
  };

  const editWorkout = (workout) => {
    // Convert Supabase format to form format
    const exercisesText = Array.isArray(workout.exercises) 
      ? workout.exercises.map(e => `${e.name} | ${e.sets} | ${e.reps} | ${e.rest}`).join('\n') 
      : workout.exercises || '';
    
    const equipmentText = Array.isArray(workout.equipment)
      ? workout.equipment.join(', ')
      : workout.equipment || '';
    
    setWorkoutForm({
      title: workout.title || '',
      description: workout.description || '',
      duration: workout.duration || '',
      difficulty: workout.difficulty || '',
      equipment: equipmentText,
      exercises: exercisesText,
      program_id: workout.program_id || '',
      week_number: workout.week_number || '',
      day_number: workout.day_number || '',
      image: workout.image || ''
    });
    setEditingItem(workout);
    setShowWorkoutModal(true);
  };

  const editResource = (resource) => {
    // Convert Supabase format to form format
    const tagsString = Array.isArray(resource.tags) 
      ? resource.tags.join(', ') 
      : (resource.tags || '');
    
    setResourceForm({
      title: resource.title || '',
      category: resource.category || 'lifestyle',
      type: resource.type || 'guide',
      description: resource.description || '',
      content: resource.content || '',
      readTime: resource.read_time || resource.readTime || '',
      downloadable: resource.downloadable || false,
      featured: resource.featured || false,
      tags: tagsString,
      link: resource.link || '',
      image: resource.image_url || resource.image || '',
      author: resource.author || ''
    });
    setEditingItem(resource);
    setShowResourceModal(true);
  };

  const deleteRecipe = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    
    try {
      const result = await deleteRecipeFromDB(id);
      if (result.success) {
        alert('Recipe deleted successfully!');
        loadRecipes();
        window.dispatchEvent(new CustomEvent('recipesUpdated'));
      } else {
        alert('Failed to delete recipe: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      alert('Error deleting recipe. Please try again.');
    }
  };

  const deleteWorkout = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workout?')) return;
    
    try {
      const result = await deleteWorkoutFromDB(id);
      if (result.success) {
        alert('Workout deleted successfully!');
        await loadWorkouts();
      } else {
        alert('Failed to delete workout: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting workout:', error);
      alert('Error deleting workout. Please try again.');
    }
  };

  const deleteProgram = async (id) => {
    if (!window.confirm('Are you sure you want to delete this program? This will also delete all workout history for this program.')) return;
    
    try {
      const result = await deleteProgramFromDB(id);
      if (result.success) {
        alert('Program deleted successfully!');
        loadPrograms();
        window.dispatchEvent(new CustomEvent('programsUpdated'));
      } else {
        alert('Failed to delete program: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting program:', error);
      alert('Error deleting program. Please try again.');
    }
  };

  const archiveProgram = async (program) => {
    const newStatus = program.status === 'archived' ? 'active' : 'archived';
    const action = newStatus === 'archived' ? 'archive' : 'restore';
    
    try {
      // Only update the status field - don't pass frontend-only fields
      const result = await updateProgram(program.id, { status: newStatus });
      if (result.success) {
        alert(`Program ${action}d successfully!`);
        loadPrograms();
        window.dispatchEvent(new CustomEvent('programsUpdated'));
      } else {
        alert(`Failed to ${action} program: ` + result.error);
      }
    } catch (error) {
      console.error(`Error ${action}ing program:`, error);
      alert(`Error ${action}ing program. Please try again.`);
    }
  };

  const editProgram = (program) => {
    setProgramForm(program);
    setEditingItem(program);
    setShowProgramModal(true);
  };

  const duplicateProgram = async (program) => {
    try {
      // Auto-increment month in title
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      let newTitle = program.title;
      
      // Try to detect and increment month
      monthNames.forEach((month, idx) => {
        if (program.title.includes(month)) {
          const nextMonth = monthNames[(idx + 1) % 12];
          newTitle = program.title.replace(month, nextMonth);
          
          // Increment year if we wrapped from December to January
          if (idx === 11) {
            const yearMatch = program.title.match(/\b(\d{4})\b/);
            if (yearMatch) {
              const currentYear = parseInt(yearMatch[1]);
              newTitle = newTitle.replace(yearMatch[1], (currentYear + 1).toString());
            }
          }
        }
      });
      
      // If no month found, just append "Copy"
      if (newTitle === program.title) {
        newTitle = `${program.title} (Copy)`;
      }
      
      // Create duplicate with new title
      const { id, created_at, updated_at, progress_reset_at, ...programWithoutIds } = program;
      const duplicatedProgram = {
        ...programWithoutIds,
        title: newTitle,
        status: 'active'
        // id, created_at, updated_at, progress_reset_at are excluded - Supabase will auto-generate
      };
      
      // Open in SimpleProgramBuilder with duplicated data
      // Pass as editingItem so SimpleProgramBuilder receives it
      setEditingItem(duplicatedProgram);
      setShowProgramModal(true);
      
      alert(`✅ Program duplicated! New title: "${newTitle}"\n\nMake any changes you need, then click "Create Program" to save.`);
    } catch (error) {
      console.error('Error duplicating program:', error);
      alert('Error duplicating program. Please try again.');
    }
  };

  const handleProgramSubmit = async (e) => {
    e.preventDefault();
    
    if (!programForm.title || !programForm.description) {
      alert('Please fill in program title and description');
      return;
    }

    try {
      // Prepare program data for Supabase
      const programData = {
        title: programForm.title,
        description: programForm.description,
        duration: `${programForm.duration || 4} weeks`,
        level: programForm.level?.toLowerCase() || 'intermediate',
        goals: typeof programForm.goals === 'string' 
          ? programForm.goals.split(',').map(g => g.trim()).filter(g => g)
          : (programForm.goals || []),
        icon: programForm.image || '💪',
        weeks: programForm.weeks || [],
        total_workouts: programForm.weeks?.reduce((sum, week) => sum + (week.workouts?.length || 0), 0) || 0,
        is_public: true
      };

      let result;
      // Only update if editingItem has a valid ID, otherwise create new
      if (editingItem && editingItem.id) {
        // Update existing program
        result = await updateProgram(editingItem.id, programData);
      } else {
        // Create new program (including duplicates)
        result = await createProgram(programData);
      }

      if (result.success) {
        alert(editingItem ? 'Program updated successfully!' : 'Program created successfully!');
        setShowProgramModal(false);
        resetProgramForm();
        // Reload programs
        loadPrograms();
        window.dispatchEvent(new CustomEvent('programsUpdated'));
      } else {
        alert('Failed to save program: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving program:', error);
      alert('Error saving program. Please try again.');
    }
  };

  const addWorkoutToWeek = () => {
    const workout = {
      ...workoutFormInProgram,
      id: workoutFormInProgram.id || `workout-${Date.now()}`,
      equipment: Array.isArray(workoutFormInProgram.equipment) 
        ? workoutFormInProgram.equipment 
        : workoutFormInProgram.equipment.split(',').map(e => e.trim()).filter(e => e)
    };
    
    const weekNumber = currentWeek + 1;
    const updatedWeeks = [...programForm.weeks];
    
    if (currentWorkout !== null) {
      // Edit existing workout
      updatedWeeks[currentWeek].workouts = updatedWeeks[currentWeek].workouts.map(w => 
        w.id === workout.id ? workout : w
      );
    } else {
      // Add new workout
      updatedWeeks[currentWeek].workouts = [...(updatedWeeks[currentWeek].workouts || []), workout];
    }
    
    setProgramForm({ ...programForm, weeks: updatedWeeks });
    
    // Reset form
    setWorkoutFormInProgram({
      id: '',
      title: '',
      day: '',
      duration: '',
      difficulty: 'Beginner',
      equipment: [],
      exercises: []
    });
    setCurrentWorkout(null);
    setCurrentWeek(null);
    setLastAddedExerciseId(null);
    
    // Show success message
    alert(`✅ Workout "${workout.title}" added to Week ${weekNumber}!\n\n⚠️ Remember to scroll down and click "Create Program" or "Update Program" to save your changes.`);
  };

  const addExerciseToWorkout = (linkToPrevious = false) => {
    let supersetGroup = exerciseForm.supersetGroup;
    
    // Auto-link to previous exercise if requested
    if (linkToPrevious && lastAddedExerciseId) {
      const lastExercise = workoutFormInProgram.exercises.find(ex => ex.id === lastAddedExerciseId);
      if (lastExercise) {
        if (lastExercise.supersetGroup) {
          // Increment the number (A1 -> A2, B1 -> B2)
          const letter = lastExercise.supersetGroup.charAt(0);
          const number = parseInt(lastExercise.supersetGroup.charAt(1)) || 1;
          supersetGroup = `${letter}${number + 1}`;
        } else {
          // Create new superset group
          const existingGroups = workoutFormInProgram.exercises
            .map(ex => ex.supersetGroup)
            .filter(g => g)
            .map(g => g.charAt(0));
          const nextLetter = existingGroups.length === 0 ? 'A' : 
            String.fromCharCode(Math.max(...existingGroups.map(l => l.charCodeAt(0))) + 1);
          
          // Update previous exercise to A1
          const updatedExercises = workoutFormInProgram.exercises.map(ex =>
            ex.id === lastAddedExerciseId ? { ...ex, supersetGroup: `${nextLetter}1` } : ex
          );
          setWorkoutFormInProgram({
            ...workoutFormInProgram,
            exercises: updatedExercises
          });
          supersetGroup = `${nextLetter}2`;
        }
      }
    }
    
    const exercise = {
      ...exerciseForm,
      id: exerciseForm.id || `ex-${Date.now()}`,
      supersetGroup: supersetGroup,
      muscleGroups: Array.isArray(exerciseForm.muscleGroups)
        ? exerciseForm.muscleGroups
        : exerciseForm.muscleGroups.split(',').map(m => m.trim()).filter(m => m)
    };
    
    setWorkoutFormInProgram({
      ...workoutFormInProgram,
      exercises: [...workoutFormInProgram.exercises, exercise]
    });
    
    setLastAddedExerciseId(exercise.id);
    setExerciseSearchQuery('');
    setExerciseForm({
      id: '',
      name: '',
      supersetGroup: '',
      sets: [
        { setNumber: 1, reps: '10-12', type: 'normal', isTimed: false }
      ],
      tempo: '',
      restTime: '60s',
      instructions: '',
      muscleGroups: [],
      needsWeight: false,
      videoUrl: ''
    });
    setUseLibraryExercise(false);
  };

  const saveExerciseToLibrary = () => {
    const libraryExercise = {
      id: `lib-${Date.now()}`,
      name: exerciseForm.name,
      muscleGroups: Array.isArray(exerciseForm.muscleGroups)
        ? exerciseForm.muscleGroups
        : exerciseForm.muscleGroups.split(',').map(m => m.trim()).filter(m => m),
      videoUrl: exerciseForm.videoUrl,
      instructions: exerciseForm.instructions,
      needsWeight: exerciseForm.needsWeight,
      defaultRestTime: exerciseForm.restTime,
      defaultTempo: exerciseForm.tempo
    };

    const updatedLibrary = [...exerciseLibrary, libraryExercise];
    setExerciseLibrary(updatedLibrary);
    localStorage.setItem('exerciseLibrary', JSON.stringify(updatedLibrary));
    alert(`✅ "${exerciseForm.name}" saved to Exercise Library!`);
  };

  const loadExerciseFromLibrary = (libraryExercise) => {
    setExerciseForm({
      ...exerciseForm,
      name: libraryExercise.name,
      muscleGroups: libraryExercise.muscleGroups,
      videoUrl: libraryExercise.videoUrl || '',
      instructions: libraryExercise.instructions || '',
      needsWeight: libraryExercise.needsWeight || false,
      restTime: libraryExercise.defaultRestTime || '60s',
      tempo: libraryExercise.defaultTempo || ''
    });
    setUseLibraryExercise(false);
  };

  const deleteExerciseFromLibrary = (id) => {
    if (window.confirm('Delete this exercise from library?')) {
      const updatedLibrary = exerciseLibrary.filter(ex => ex.id !== id);
      setExerciseLibrary(updatedLibrary);
      localStorage.setItem('exerciseLibrary', JSON.stringify(updatedLibrary));
    }
  };

  const removeExerciseFromWorkout = (exerciseId) => {
    setWorkoutFormInProgram({
      ...workoutFormInProgram,
      exercises: workoutFormInProgram.exercises.filter(e => e.id !== exerciseId)
    });
  };

  const editExerciseInWorkout = (exercise) => {
    // Load exercise into form for editing
    setExerciseForm({
      ...exercise,
      muscleGroups: Array.isArray(exercise.muscleGroups) 
        ? exercise.muscleGroups 
        : exercise.muscleGroups.split(',').map(m => m.trim()).filter(m => m)
    });
    setExerciseSearchQuery(exercise.name);
    
    // Remove from workout temporarily (will be re-added when user clicks "Add Exercise")
    removeExerciseFromWorkout(exercise.id);
    
    // Scroll to exercise form
    setTimeout(() => {
      const exerciseForm = document.querySelector('.bg-orange-50');
      if (exerciseForm) {
        exerciseForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const moveExerciseUp = (index) => {
    if (index === 0) return; // Already at top
    const exercises = [...workoutFormInProgram.exercises];
    [exercises[index - 1], exercises[index]] = [exercises[index], exercises[index - 1]];
    setWorkoutFormInProgram({
      ...workoutFormInProgram,
      exercises
    });
  };

  const moveExerciseDown = (index) => {
    if (index === workoutFormInProgram.exercises.length - 1) return; // Already at bottom
    const exercises = [...workoutFormInProgram.exercises];
    [exercises[index], exercises[index + 1]] = [exercises[index + 1], exercises[index]];
    setWorkoutFormInProgram({
      ...workoutFormInProgram,
      exercises
    });
  };

  const editWorkoutInWeek = (weekIndex, workoutIndex) => {
    const workout = programForm.weeks[weekIndex].workouts[workoutIndex];
    setCurrentWeek(weekIndex);
    setCurrentWorkout(workoutIndex);
    setWorkoutFormInProgram(workout);
  };

  const duplicateWeek = (sourceWeekIndex, targetWeekIndex) => {
    const sourceWeek = programForm.weeks[sourceWeekIndex];
    const updatedWeeks = [...programForm.weeks];
    
    // Deep copy workouts with new IDs to avoid conflicts
    const duplicatedWorkouts = sourceWeek.workouts.map(workout => ({
      ...workout,
      id: `${Date.now()}-${Math.random()}`, // Generate unique ID
      exercises: workout.exercises.map(exercise => ({
        ...exercise,
        id: `${Date.now()}-${Math.random()}`, // New exercise ID
        sets: exercise.sets.map(set => ({ ...set })) // Deep copy sets
      }))
    }));
    
    updatedWeeks[targetWeekIndex] = {
      ...updatedWeeks[targetWeekIndex],
      workouts: duplicatedWorkouts,
      title: updatedWeeks[targetWeekIndex].title || sourceWeek.title
    };
    
    setProgramForm({ ...programForm, weeks: updatedWeeks });
  };

  const deleteWorkoutFromWeek = (weekIndex, workoutIndex) => {
    if (window.confirm('Delete this workout?')) {
      const updatedWeeks = [...programForm.weeks];
      updatedWeeks[weekIndex].workouts = updatedWeeks[weekIndex].workouts.filter((_, i) => i !== workoutIndex);
      setProgramForm({ ...programForm, weeks: updatedWeeks });
    }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const result = await deleteResourceFromDB(id);
      if (result.success) {
        alert('Resource deleted successfully!');
        loadResources();
        window.dispatchEvent(new CustomEvent('resourcesUpdated'));
      } else {
        alert('Failed to delete resource: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Error deleting resource. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-800">Admin Dashboard</h1>
            <p className="text-slate-600 mt-2">Manage recipes and workouts for the Alera community</p>
            
            {/* Storage Usage Indicator */}
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Storage Usage:</span>
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      storageUsage > 80 ? 'bg-red-500' : storageUsage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${storageUsage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-slate-600">{Math.round(storageUsage)}%</span>
              </div>
              {storageUsage > 80 && (
                <p className="text-xs text-red-600 mt-1">Storage almost full! Consider using smaller images or clearing old data.</p>
              )}

      {/* Resource Modal */}
      {showResourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit Resource' : 'Add New Resource'}
              </h3>
            </div>
            <form onSubmit={handleResourceSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Resource Title"
                  value={resourceForm.title}
                  onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                  required
                />
                <input
                  type="text"
                  placeholder="Read Time (e.g., 8 min read)"
                  value={resourceForm.readTime}
                  onChange={(e) => setResourceForm({...resourceForm, readTime: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <select
                  value={resourceForm.category}
                  onChange={(e) => setResourceForm({...resourceForm, category: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="eczema">Eczema Relief</option>
                  <option value="allergies">Allergy Management</option>
                  <option value="lifestyle">Lifestyle Tips</option>
                  <option value="products">Recipe Books</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={resourceForm.type}
                  onChange={(e) => setResourceForm({...resourceForm, type: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="article">Article</option>
                  <option value="guide">Guide</option>
                  <option value="story">Personal Story</option>
                  <option value="research">Research</option>
                  <option value="tip">Quick Tip</option>
                  <option value="checklist">Checklist</option>
                  <option value="ebook">E-book</option>
                  <option value="video">Video</option>
                </select>
                <input
                  type="text"
                  placeholder="External Link (optional)"
                  value={resourceForm.link}
                  onChange={(e) => setResourceForm({...resourceForm, link: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
              </div>

              {/* Resource Image (optional) */}
              <div>
                <input
                  type="text"
                  placeholder="Image URL or Emoji (optional)"
                  value={resourceForm.image}
                  onChange={(e) => setResourceForm({...resourceForm, image: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2 w-full mb-2"
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Or upload:</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const img = new Image();
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            // Max dimensions
                            const maxWidth = 800;
                            const maxHeight = 600;
                            let { width, height } = img;
                            if (width > height) {
                              if (width > maxWidth) {
                                height = (height * maxWidth) / width;
                                width = maxWidth;
                              }
                            } else {
                              if (height > maxHeight) {
                                width = (width * maxHeight) / height;
                                height = maxHeight;
                              }
                            }
                            canvas.width = width;
                            canvas.height = height;
                            ctx.drawImage(img, 0, 0, width, height);
                            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                            
                            // Check compressed size
                            const compressedSize = compressedDataUrl.length * 0.75; // Approximate base64 to bytes
                            if (compressedSize > 1024 * 1024) {
                              alert('Image is still too large after compression. Please use a smaller image or lower resolution.');
                              return;
                            }
                            
                            setResourceForm({...resourceForm, image: compressedDataUrl});
                          };
                          img.src = event.target?.result;
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="text-sm text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                {resourceForm.image && (
                  <div className="mt-2">
                    {(resourceForm.image.startsWith('data:') || resourceForm.image.startsWith('http')) ? (
                      <img
                        src={resourceForm.image}
                        alt="Resource preview"
                        className="w-40 h-28 object-cover rounded border"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center text-2xl">
                        {resourceForm.image}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <textarea
                placeholder="Short Description"
                value={resourceForm.description}
                onChange={(e) => setResourceForm({...resourceForm, description: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full h-24"
                rows="4"
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content (optional)</label>
                
                {/* Visual Rich Text Editor Toolbar */}
                <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border border-slate-300 rounded-t-lg border-b-0">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('bold', false, null);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 font-bold text-sm"
                    title="Bold (select text first)"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('italic', false, null);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 italic text-sm"
                    title="Italic (select text first)"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('underline', false, null);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 underline text-sm"
                    title="Underline (select text first)"
                  >
                    U
                  </button>
                  <div className="w-px bg-slate-300 mx-1"></div>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('formatBlock', false, 'h2');
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm font-semibold"
                    title="Heading"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('formatBlock', false, 'h3');
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm font-medium"
                    title="Subheading"
                  >
                    H3
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('formatBlock', false, 'p');
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm"
                    title="Normal text"
                  >
                    ¶
                  </button>
                  <div className="w-px bg-slate-300 mx-1"></div>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const editor = document.getElementById('resource-content-editor');
                      if (editor) {
                        editor.focus();
                        const selection = window.getSelection();
                        const selectedText = selection.toString().trim();
                        
                        if (selectedText) {
                          // Convert selected text to bullet list
                          const lines = selectedText.split('\n').filter(line => line.trim());
                          const listItems = lines.map(line => `<li>${line.trim()}</li>`).join('');
                          document.execCommand('insertHTML', false, `<ul style="margin-left: 20px; padding-left: 20px; list-style-type: disc;">${listItems}</ul>`);
                        } else {
                          // Insert new bullet list
                          document.execCommand('insertHTML', false, '<ul style="margin-left: 20px; padding-left: 20px; list-style-type: disc;"><li><br></li></ul>');
                        }
                      }
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm"
                    title="Bullet List - Click to insert or select text to convert"
                  >
                    • List
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const editor = document.getElementById('resource-content-editor');
                      if (editor) {
                        editor.focus();
                        const selection = window.getSelection();
                        const selectedText = selection.toString().trim();
                        
                        if (selectedText) {
                          // Convert selected text to numbered list
                          const lines = selectedText.split('\n').filter(line => line.trim());
                          const listItems = lines.map(line => `<li>${line.trim()}</li>`).join('');
                          document.execCommand('insertHTML', false, `<ol style="margin-left: 20px; padding-left: 20px;">${listItems}</ol>`);
                        } else {
                          // Insert new numbered list
                          document.execCommand('insertHTML', false, '<ol style="margin-left: 20px; padding-left: 20px;"><li><br></li></ol>');
                        }
                      }
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm"
                    title="Numbered List - Click to insert or select text to convert"
                  >
                    1. List
                  </button>
                  <div className="w-px bg-slate-300 mx-1"></div>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const url = prompt('Enter link URL:', 'https://');
                      if (url) {
                        document.getElementById('resource-content-editor')?.focus();
                        document.execCommand('createLink', false, url);
                      }
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm text-blue-600 underline"
                    title="Add Link"
                  >
                    Link
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      document.getElementById('resource-content-editor')?.focus();
                      document.execCommand('insertHorizontalRule', false, null);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm"
                    title="Horizontal Line"
                  >
                    ─
                  </button>
                  <div className="w-px bg-slate-300 mx-1"></div>
                  <label
                    className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 text-sm cursor-pointer flex items-center gap-1"
                    title="Insert Image"
                  >
                    🖼️ Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const img = new Image();
                            img.onload = () => {
                              const canvas = document.createElement('canvas');
                              const ctx = canvas.getContext('2d');
                              const maxWidth = 500;
                              const maxHeight = 350;
                              let { width, height } = img;
                              if (width > maxWidth) {
                                height = (height * maxWidth) / width;
                                width = maxWidth;
                              }
                              if (height > maxHeight) {
                                width = (width * maxHeight) / height;
                                height = maxHeight;
                              }
                              canvas.width = width;
                              canvas.height = height;
                              ctx.drawImage(img, 0, 0, width, height);
                              const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                              
                              const compressedSize = compressedDataUrl.length * 0.75;
                              if (compressedSize > 500 * 1024) {
                                alert('Image is too large. Please use a smaller image.');
                                return;
                              }
                              
                              document.execCommand('insertImage', false, compressedDataUrl);
                            };
                            img.src = event.target?.result;
                          };
                          reader.readAsDataURL(file);
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                
                {/* CSS for editor lists */}
                <style>{`
                  #resource-content-editor ul {
                    list-style-type: disc !important;
                    margin-left: 20px !important;
                    padding-left: 20px !important;
                    list-style-position: outside !important;
                  }
                  #resource-content-editor ol {
                    list-style-type: decimal !important;
                    margin-left: 20px !important;
                    padding-left: 20px !important;
                    list-style-position: outside !important;
                  }
                  #resource-content-editor li {
                    margin-bottom: 4px !important;
                    display: list-item !important;
                  }
                `}</style>
                
                {/* Visual Editor - What You See Is What You Get */}
                <div
                  id="resource-content-editor"
                  contentEditable
                  className="border border-slate-300 rounded-b-lg px-4 py-3 w-full min-h-[200px] max-h-[400px] overflow-y-auto text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ lineHeight: '1.6', fontFamily: 'Nunito Sans, Inter, sans-serif' }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    const formattedText = text.split('\n').map(line => line.trim()).filter(line => line).join('<br><br>');
                    document.execCommand('insertHTML', false, formattedText);
                  }}
                  suppressContentEditableWarning={true}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Select text and click buttons to format. Pasted text is automatically cleaned up.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Tags (comma separated: eczema, routine)"
                  value={resourceForm.tags}
                  onChange={(e) => setResourceForm({...resourceForm, tags: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Author (e.g., By Kate)"
                  value={resourceForm.author}
                  onChange={(e) => setResourceForm({...resourceForm, author: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={resourceForm.downloadable}
                      onChange={(e) => setResourceForm({...resourceForm, downloadable: e.target.checked})}
                    />
                    Downloadable
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={resourceForm.featured}
                      onChange={(e) => setResourceForm({...resourceForm, featured: e.target.checked})}
                    />
                    Featured
                  </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
                  {editingItem ? 'Update Resource' : 'Add Resource'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResourceModal(false);
                    resetResourceForm();
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
            </div>
          </div>
          
          <div className="flex gap-3">
            {storageUsage > 50 && (
              <button
                onClick={clearAllStorage}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                Clear Storage
              </button>
            )}
            <button
              onClick={async () => {
                await signOut();
                navigate('/admin-login');
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">🍽️</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">{recipes.length}</h3>
                <p className="text-slate-600">Total Recipes</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">🏋️</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">{programs.length}</h3>
                <p className="text-slate-600">Programs</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">💪</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">{workouts.length}</h3>
                <p className="text-slate-600">Workouts</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">📚</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">{resources.length}</h3>
                <p className="text-slate-600">Resources</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('recipes')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'recipes'
                    ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                🍽️ Recipes ({recipes.length})
              </button>
              <button
                onClick={() => setActiveTab('programs')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'programs'
                    ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                🏋️ Programs ({programs.length})
              </button>
              <button
                onClick={() => setActiveTab('workouts')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'workouts'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                💪 Workouts ({workouts.length})
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'resources'
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                📚 Resources ({resources.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'recipes' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-800">Recipe Management</h2>
                  <button
                    onClick={() => setShowRecipeModal(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    + Add Recipe
                  </button>
                </div>

                {/* Category Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  {['breakfast', 'lunch', 'dinner', 'snacks', 'treats'].map(category => {
                    const count = recipes.filter(r => r.meal_type === category || r.mealType === category).length;
                    return (
                      <div key={category} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                        <div className="text-2xl font-bold text-green-700">{count}</div>
                        <div className="text-sm text-green-600 capitalize">{category}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Grouped by Category */}
                {['breakfast', 'lunch', 'dinner', 'snacks', 'treats'].map(category => {
                  const categoryRecipes = recipes.filter(r => r.meal_type === category || r.mealType === category);
                  if (categoryRecipes.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-8">
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 capitalize flex items-center gap-2">
                        <span className="text-2xl">
                          {category === 'breakfast' ? '🍳' : category === 'lunch' ? '🥗' : category === 'dinner' ? '🍽️' : category === 'snack' ? '🍪' : '🍰'}
                        </span>
                        {category} ({categoryRecipes.length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryRecipes.map((recipe) => (
                          <div key={recipe.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <h4 className="font-semibold text-slate-800 mb-2">{recipe.title}</h4>
                            <p className="text-sm text-slate-600 mb-3">
                              {recipe.prep_time || recipe.prepTime} • {recipe.servings} servings • {recipe.difficulty}
                            </p>
                            {(recipe.calories || recipe.protein) && (
                              <p className="text-xs text-slate-500 mb-3">
                                {recipe.calories && `${recipe.calories} cal`}
                                {recipe.protein && ` • ${recipe.protein}g protein`}
                              </p>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => editRecipe(recipe)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteRecipe(recipe.id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Uncategorized recipes - show if they don't have meal_type set */}
                {(() => {
                  const uncategorized = recipes.filter(r => !r.meal_type && !r.mealType);
                  if (uncategorized.length === 0) return null;
                  
                  return (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <span className="text-2xl">📋</span>
                        Uncategorized ({uncategorized.length})
                        <span className="text-xs text-slate-500 font-normal ml-2">
                          (Edit these recipes to add a meal type)
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {uncategorized.map((recipe) => (
                          <div key={recipe.id} className="border border-orange-200 bg-orange-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <h4 className="font-semibold text-slate-800 mb-2">{recipe.title}</h4>
                            <p className="text-sm text-slate-600 mb-3">
                              {recipe.prep_time || recipe.prepTime} • {recipe.servings} servings • {recipe.difficulty}
                            </p>
                            {(recipe.calories || recipe.protein) && (
                              <p className="text-xs text-slate-500 mb-3">
                                {recipe.calories && `${recipe.calories} cal`}
                                {recipe.protein && ` • ${recipe.protein}g protein`}
                              </p>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => editRecipe(recipe)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteRecipe(recipe.id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'resources' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-800">Resource Management</h2>
                  <button
                    onClick={() => setShowResourceModal(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    + Add Resource
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {resources.map((resource) => (
                    <div key={resource.id} className="border border-slate-200 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-800 mb-1">{resource.title}</h3>
                      <p className="text-sm text-slate-600 mb-2">{resource.type} • {resource.category}</p>
                      <p className="text-sm text-slate-600 mb-3 truncate">{resource.description}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => editResource(resource)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteResource(resource.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'programs' && (
              <div>
                {/* Active Programs Section */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-800">Active Programs</h2>
                  <button
                    onClick={() => setShowProgramModal(true)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    + Build New Program
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {programs.filter(p => p.status !== 'archived').map((program) => (
                    <div key={program.id} className="border border-slate-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{program.icon}</span>
                        <h3 className="font-semibold text-slate-800">{program.title}</h3>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        {program.duration} • {program.frequency} • {program.level}
                      </p>
                      <p className="text-xs text-slate-500 mb-3">
                        {program.totalWorkouts} workouts across {program.weeks.length} weeks
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => duplicateProgram(program)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          📋 Duplicate
                        </button>
                        <button
                          onClick={() => editProgram(program)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => archiveProgram(program)}
                          className="text-orange-600 hover:text-orange-800 text-sm"
                        >
                          Archive
                        </button>
                        <button
                          onClick={() => deleteProgram(program.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {programs.filter(p => p.status !== 'archived').length === 0 && (
                    <div className="col-span-full text-center py-8 text-slate-500">
                      No active programs. Create one or restore from archived.
                    </div>
                  )}
                </div>

                {/* Archived Programs Folder */}
                {programs.filter(p => p.status === 'archived').length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowArchivedPrograms(!showArchivedPrograms)}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">📁</span>
                        <span className="font-semibold text-slate-700">Archived Programs</span>
                        <span className="text-sm text-slate-500">({programs.filter(p => p.status === 'archived').length})</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-slate-500 transition-transform ${showArchivedPrograms ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showArchivedPrograms && (
                      <div className="p-4 bg-slate-50 border-t border-slate-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {programs.filter(p => p.status === 'archived').map((program) => (
                            <div key={program.id} className="border border-slate-200 rounded-lg p-4 bg-white">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl opacity-60">{program.icon}</span>
                                <h3 className="font-semibold text-slate-600">{program.title}</h3>
                              </div>
                              <p className="text-sm text-slate-500 mb-2">
                                {program.duration} • {program.frequency} • {program.level}
                              </p>
                              <p className="text-xs text-slate-400 mb-3">
                                {program.totalWorkouts} workouts across {program.weeks.length} weeks
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  onClick={() => duplicateProgram(program)}
                                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                                >
                                  📋 Duplicate
                                </button>
                                <button
                                  onClick={() => archiveProgram(program)}
                                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                                >
                                  ↑ Restore
                                </button>
                                <button
                                  onClick={() => editProgram(program)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteProgram(program.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'workouts' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-800">Workout Management</h2>
                  <button
                    onClick={() => setShowWorkoutModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    + Add Workout
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workouts.map((workout) => (
                    <div key={workout.id} className="border border-slate-200 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-800 mb-2">{workout.name}</h3>
                      <p className="text-sm text-slate-600 mb-3">
                        {workout.duration} • {workout.difficulty}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => editWorkout(workout)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteWorkout(workout.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recipe Modal */}
      {showRecipeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit Recipe' : 'Add New Recipe'}
              </h3>
            </div>
            <form onSubmit={handleRecipeSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Recipe Title"
                  value={recipeForm.title}
                  onChange={(e) => setRecipeForm({...recipeForm, title: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                  required
                />
                <div>
                  <input
                    type="text"
                    placeholder="Image URL or Emoji"
                    value={recipeForm.image}
                    onChange={(e) => setRecipeForm({...recipeForm, image: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2 w-full mb-2"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Or upload:</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Show loading state
                          const originalImage = recipeForm.image;
                          setRecipeForm({...recipeForm, image: '⏳ Uploading...'});
                          
                          // Upload to Supabase Storage with compression
                          const result = await uploadImageWithCompression(file, 'recipes');
                          
                          if (result.success) {
                            setRecipeForm({...recipeForm, image: result.url});
                          } else {
                            alert('Failed to upload image: ' + result.error);
                            setRecipeForm({...recipeForm, image: originalImage});
                          }
                        }
                      }}
                      className="text-sm text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  {recipeForm.image && (
                    <div className="mt-2">
                      {recipeForm.image.startsWith('data:') || recipeForm.image.startsWith('http') ? (
                        <img 
                          src={recipeForm.image} 
                          alt="Recipe preview" 
                          className="w-20 h-20 object-cover rounded border"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center text-2xl">
                          {recipeForm.image}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Prep Time"
                  value={recipeForm.prepTime}
                  onChange={(e) => setRecipeForm({...recipeForm, prepTime: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
                <input
                  type="number"
                  placeholder="Servings"
                  value={recipeForm.servings}
                  onChange={(e) => setRecipeForm({...recipeForm, servings: parseInt(e.target.value)})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
                <select
                  value={recipeForm.difficulty}
                  onChange={(e) => setRecipeForm({...recipeForm, difficulty: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              <select
                value={recipeForm.mealType}
                onChange={(e) => setRecipeForm({...recipeForm, mealType: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full"
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snacks">Snacks</option>
                <option value="treats">Treats</option>
              </select>
              {/* Dietary Requirements Checkboxes */}
              <div className="border border-slate-300 rounded-lg px-3 py-3 w-full">
                <label className="block text-sm font-medium text-slate-700 mb-2">Dietary Tags</label>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {[
                    'gluten-free', 'dairy-free', 'egg-free', 'soy-free', 
                    'peanut-free', 'tree-nut-free', 'pea-free', 'sesame-free',
                    'shellfish-free', 'fish-free', 'sugar-free',
                    'vegan', 'vegetarian', 'anti-inflammatory'
                  ].map(tag => {
                    const dietaryArray = recipeForm.dietary ? recipeForm.dietary.split(',').map(d => d.trim().toLowerCase()) : [];
                    const isChecked = dietaryArray.includes(tag);
                    return (
                      <label key={tag} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            let newDietary = dietaryArray.filter(d => d && d !== tag);
                            if (e.target.checked) newDietary.push(tag);
                            setRecipeForm({...recipeForm, dietary: newDietary.join(', ')});
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-sage-600 focus:ring-sage-500"
                        />
                        <span className="text-sm text-slate-600 capitalize">{tag.replace(/-/g, ' ')}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <input
                type="text"
                placeholder="Nutrition Highlights (comma separated)"
                value={recipeForm.nutritionHighlights}
                onChange={(e) => setRecipeForm({...recipeForm, nutritionHighlights: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full"
              />
              
              {/* Nutrition Information */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Nutrition Per Serving</h4>
                <div className="grid grid-cols-4 gap-4">
                  <input
                    type="number"
                    placeholder="Calories"
                    value={recipeForm.calories}
                    onChange={(e) => setRecipeForm({...recipeForm, calories: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Protein (g)"
                    value={recipeForm.protein}
                    onChange={(e) => setRecipeForm({...recipeForm, protein: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Carbs (g)"
                    value={recipeForm.carbs}
                    onChange={(e) => setRecipeForm({...recipeForm, carbs: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Fats (g)"
                    value={recipeForm.fats}
                    onChange={(e) => setRecipeForm({...recipeForm, fats: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <textarea
                placeholder="Ingredients (one per line: name | amount | category)"
                value={recipeForm.ingredients}
                onChange={(e) => setRecipeForm({...recipeForm, ingredients: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full h-24"
                rows="4"
              />
              <textarea
                placeholder="Instructions (one per line)"
                value={recipeForm.instructions}
                onChange={(e) => setRecipeForm({...recipeForm, instructions: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full h-24"
                rows="4"
              />
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  {editingItem ? 'Update Recipe' : 'Add Recipe'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRecipeModal(false);
                    resetRecipeForm();
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Workout Modal */}
      {showWorkoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit Workout' : 'Add New Workout'}
              </h3>
            </div>
            <form onSubmit={handleWorkoutSubmit} className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Workout Name"
                value={workoutForm.name}
                onChange={(e) => setWorkoutForm({...workoutForm, name: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full"
                required
              />
              <div>
                <input
                  type="text"
                  placeholder="Image URL or Emoji"
                  value={workoutForm.image}
                  onChange={(e) => setWorkoutForm({...workoutForm, image: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2 w-full mb-2"
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Or upload:</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (limit to 1MB)
                        if (file.size > 1024 * 1024) {
                          alert('Image is too large! Please use an image smaller than 1MB.');
                          return;
                        }
                        
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const img = new Image();
                          img.onload = () => {
                            // Create canvas to compress image
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            
                            // Set max dimensions (smaller for more storage)
                            const maxWidth = 200;
                            const maxHeight = 200;
                            let { width, height } = img;
                            
                            // Calculate new dimensions
                            if (width > height) {
                              if (width > maxWidth) {
                                height = (height * maxWidth) / width;
                                width = maxWidth;
                              }
                            } else {
                              if (height > maxHeight) {
                                width = (width * maxHeight) / height;
                                height = maxHeight;
                              }
                            }
                            
                            canvas.width = width;
                            canvas.height = height;
                            
                            // Draw and compress (lower quality for smaller size)
                            ctx.drawImage(img, 0, 0, width, height);
                            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.5);
                            
                            setWorkoutForm({...workoutForm, image: compressedDataUrl});
                          };
                          img.src = event.target?.result;
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="text-sm text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                {workoutForm.image && (
                  <div className="mt-2">
                    {workoutForm.image.startsWith('data:') || workoutForm.image.startsWith('http') ? (
                      <img 
                        src={workoutForm.image} 
                        alt="Workout preview" 
                        className="w-20 h-20 object-cover rounded border"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center text-2xl">
                        {workoutForm.image}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <textarea
                placeholder="Workout Description"
                value={workoutForm.description}
                onChange={(e) => setWorkoutForm({...workoutForm, description: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full h-20"
                rows="3"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Duration (e.g., 30 mins)"
                  value={workoutForm.duration}
                  onChange={(e) => setWorkoutForm({...workoutForm, duration: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                />
                <select
                  value={workoutForm.difficulty}
                  onChange={(e) => setWorkoutForm({...workoutForm, difficulty: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <input
                type="text"
                placeholder="Equipment Needed"
                value={workoutForm.equipment}
                onChange={(e) => setWorkoutForm({...workoutForm, equipment: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full"
              />
              <input
                type="text"
                placeholder="Target Muscles"
                value={workoutForm.targetMuscles}
                onChange={(e) => setWorkoutForm({...workoutForm, targetMuscles: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full"
              />
              <textarea
                placeholder="Exercises (one per line: name | sets | reps | rest)"
                value={workoutForm.exercises}
                onChange={(e) => setWorkoutForm({...workoutForm, exercises: e.target.value})}
                className="border border-slate-300 rounded-lg px-3 py-2 w-full h-32"
                rows="6"
              />
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  {editingItem ? 'Update Workout' : 'Add Workout'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowWorkoutModal(false);
                    resetWorkoutForm();
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Simplified Program Builder */}
      {showProgramModal && (
        <SimpleProgramBuilder
          editingProgram={editingItem}
          onClose={() => {
            setShowProgramModal(false);
            resetProgramForm();
          }}
          onSave={async (program) => {
            try {
              // Remove image_url if it exists (not in database schema)
              const { image_url, ...cleanProgram } = program;
              
              // Only update if editingItem has a valid ID, otherwise create new
              if (editingItem && editingItem.id) {
                // Only include fields that exist in the database schema
                const validFields = {
                  title: cleanProgram.title,
                  description: cleanProgram.description,
                  duration: cleanProgram.duration,
                  frequency: cleanProgram.frequency || '3x/week',
                  level: cleanProgram.level,
                  equipment: cleanProgram.equipment || 'dumbbells',
                  goals: Array.isArray(cleanProgram.goals) ? cleanProgram.goals : (cleanProgram.goals ? cleanProgram.goals.split(',').map(g => g.trim()) : []),
                  icon: cleanProgram.icon || '🏋️',
                  icon_bg: cleanProgram.icon_bg || 'bg-orange-100',
                  color: cleanProgram.color || 'orange',
                  total_workouts: cleanProgram.weeks?.reduce((sum, week) => sum + (week.workouts?.length || 0), 0) || 0,
                  weeks: cleanProgram.weeks,
                  is_public: true,
                  status: 'active',
                  updated_at: new Date().toISOString()
                };
                const result = await updateProgram(editingItem.id, validFields);
                if (result.success) {
                  // POST-SAVE VERIFICATION: Read back from Supabase and verify data integrity
                  const { data: verifyData } = await supabase
                    .from('workout_programs')
                    .select('*')
                    .eq('id', editingItem.id)
                    .single();
                  
                  if (verifyData) {
                    const savedWorkouts = (verifyData.weeks || []).reduce((sum, w) => sum + (w.workouts?.length || 0), 0);
                    const savedExercises = (verifyData.weeks || []).reduce((sum, w) => 
                      sum + (w.workouts || []).reduce((s, wo) => s + (wo.exercises?.length || 0), 0), 0);
                    const sentWorkouts = validFields.total_workouts;
                    const sentExercises = (validFields.weeks || []).reduce((sum, w) => 
                      sum + (w.workouts || []).reduce((s, wo) => s + (wo.exercises?.length || 0), 0), 0);
                    
                    if (savedWorkouts !== sentWorkouts || savedExercises !== sentExercises) {
                      alert(`⚠️ DATA MISMATCH!\n\nSent: ${sentWorkouts} workouts, ${sentExercises} exercises\nSaved: ${savedWorkouts} workouts, ${savedExercises} exercises\n\nYour draft is backed up in your browser. Please try saving again.`);
                    } else {
                      alert(`✅ Program updated & verified!\n\n${savedWorkouts} workouts, ${savedExercises} exercises saved correctly.`);
                      // Clear draft after successful verified save
                      localStorage.removeItem('programDraft');
                    }
                  } else {
                    alert('✅ Program updated! (Could not verify - please check the program list)');
                  }
                  setShowProgramModal(false);
                  resetProgramForm();
                  loadPrograms();
                } else {
                  throw new Error(result.error);
                }
              } else {
                // Only include fields that exist in the database schema
                const validFields = {
                  title: cleanProgram.title,
                  description: cleanProgram.description,
                  duration: cleanProgram.duration,
                  frequency: cleanProgram.frequency || '3x/week',
                  level: cleanProgram.level,
                  equipment: cleanProgram.equipment || 'dumbbells',
                  goals: Array.isArray(cleanProgram.goals) ? cleanProgram.goals : (cleanProgram.goals ? cleanProgram.goals.split(',').map(g => g.trim()) : []),
                  icon: cleanProgram.icon || '🏋️',
                  icon_bg: cleanProgram.icon_bg || 'bg-orange-100',
                  color: cleanProgram.color || 'orange',
                  total_workouts: cleanProgram.weeks?.reduce((sum, week) => sum + (week.workouts?.length || 0), 0) || 0,
                  weeks: cleanProgram.weeks,
                  is_public: true,
                  status: 'active'
                };
                const result = await createProgram(validFields);
                if (result.success) {
                  // POST-SAVE VERIFICATION for new programs
                  const savedId = result.data?.id;
                  if (savedId) {
                    const { data: verifyData } = await supabase
                      .from('workout_programs')
                      .select('*')
                      .eq('id', savedId)
                      .single();
                    
                    if (verifyData) {
                      const savedWorkouts = (verifyData.weeks || []).reduce((sum, w) => sum + (w.workouts?.length || 0), 0);
                      const savedExercises = (verifyData.weeks || []).reduce((sum, w) => 
                        sum + (w.workouts || []).reduce((s, wo) => s + (wo.exercises?.length || 0), 0), 0);
                      const sentExercises = (validFields.weeks || []).reduce((sum, w) => 
                        sum + (w.workouts || []).reduce((s, wo) => s + (wo.exercises?.length || 0), 0), 0);
                      
                      if (savedWorkouts !== validFields.total_workouts || savedExercises !== sentExercises) {
                        alert(`⚠️ DATA MISMATCH!\n\nSent: ${validFields.total_workouts} workouts, ${sentExercises} exercises\nSaved: ${savedWorkouts} workouts, ${savedExercises} exercises\n\nYour draft is backed up in your browser. Please try saving again.`);
                      } else {
                        alert(`✅ Program created & verified!\n\n${savedWorkouts} workouts, ${savedExercises} exercises saved correctly.`);
                        localStorage.removeItem('programDraft');
                      }
                    } else {
                      alert('✅ Program created! (Could not verify - please check the program list)');
                    }
                  } else {
                    alert('✅ Program created successfully!');
                  }
                  setShowProgramModal(false);
                  resetProgramForm();
                  loadPrograms();
                } else {
                  throw new Error(result.error);
                }
              }
            } catch (error) {
              console.error('Error saving program:', error);
              alert(`❌ Error saving program: ${error.message || 'Please try again.'}`);
            }
          }}
          editingProgram={editingItem}
          exerciseLibrary={exerciseLibrary}
        />
      )}

      {/* OLD Program Builder Modal - BACKUP */}
      {false && showProgramModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto my-8">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-bold text-slate-800">
                {editingItem ? 'Edit Program' : 'Build New 4-Week Program'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">Create a comprehensive workout program with 3-5 workouts per week</p>
            </div>
            
            <form onSubmit={handleProgramSubmit} className="p-6 space-y-6">
              {/* Program Metadata */}
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-lg border border-orange-200">
                <h4 className="text-lg font-semibold text-slate-800 mb-4">Program Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Program Title (e.g., Glute Builder Program)"
                    value={programForm.title}
                    onChange={(e) => setProgramForm({...programForm, title: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Icon Emoji (e.g., 🍑)"
                    value={programForm.icon}
                    onChange={(e) => setProgramForm({...programForm, icon: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                </div>
                <textarea
                  placeholder="Program Description"
                  value={programForm.description}
                  onChange={(e) => setProgramForm({...programForm, description: e.target.value})}
                  className="border border-slate-300 rounded-lg px-3 py-2 w-full h-20 mt-4"
                  rows="3"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <select
                    value={programForm.level}
                    onChange={(e) => setProgramForm({...programForm, level: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Frequency (e.g., 4 days/week)"
                    value={programForm.frequency}
                    onChange={(e) => setProgramForm({...programForm, frequency: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Equipment"
                    value={programForm.equipment}
                    onChange={(e) => setProgramForm({...programForm, equipment: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Goals (comma separated)"
                    value={programForm.goals}
                    onChange={(e) => setProgramForm({...programForm, goals: e.target.value})}
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Week Builder */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-slate-800">Build Your 4 Weeks</h4>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setProgramViewMode('list')}
                      className={`text-sm px-3 py-1 rounded ${
                        programViewMode === 'list'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      📋 List View
                    </button>
                    <button
                      type="button"
                      onClick={() => setProgramViewMode('calendar')}
                      className={`text-sm px-3 py-1 rounded ${
                        programViewMode === 'calendar'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      📅 Calendar View
                    </button>
                  </div>
                </div>

                {/* List View */}
                {programViewMode === 'list' && programForm.weeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="border border-slate-300 rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h5 className="text-md font-semibold text-slate-700">
                          Week {week.week}
                          <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                            {week.workouts?.length || 0} workout{week.workouts?.length !== 1 ? 's' : ''}
                          </span>
                        </h5>
                        {week.workouts && week.workouts.length > 0 && (
                          <div className="flex gap-2">
                            {/* Duplicate to All button */}
                            <button
                              type="button"
                              className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 flex items-center gap-1"
                              onClick={() => {
                                const targetWeeks = programForm.weeks
                                  .map((w, i) => i)
                                  .filter(i => i !== weekIndex);
                                
                                if (window.confirm(`Duplicate Week ${week.week} workouts to Weeks ${targetWeeks.map(i => i + 1).join(', ')}?`)) {
                                  targetWeeks.forEach(targetIndex => {
                                    duplicateWeek(weekIndex, targetIndex);
                                  });
                                }
                              }}
                            >
                              📋 Duplicate to All
                            </button>
                            
                            {/* Individual week buttons */}
                            {programForm.weeks.map((targetWeek, targetIndex) => 
                              targetIndex !== weekIndex && (
                                <button
                                  key={targetIndex}
                                  type="button"
                                  className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 border border-blue-200"
                                  onClick={() => {
                                    if (window.confirm(`Copy Week ${week.week} to Week ${targetWeek.week}?`)) {
                                      duplicateWeek(weekIndex, targetIndex);
                                    }
                                  }}
                                  title={`Copy to Week ${targetWeek.week}`}
                                >
                                  → W{targetWeek.week}
                                </button>
                              )
                            )}
                          </div>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Week Title (e.g., Foundation Week)"
                        value={week.title}
                        onChange={(e) => {
                          const updatedWeeks = [...programForm.weeks];
                          updatedWeeks[weekIndex].title = e.target.value;
                          setProgramForm({...programForm, weeks: updatedWeeks});
                        }}
                        className="border border-slate-300 rounded px-3 py-1 text-sm w-64"
                      />
                    </div>

                    {/* Workouts in this week */}
                    <div className="space-y-2 mb-3">
                      {week.workouts && week.workouts.length > 0 ? (
                        week.workouts.map((workout, workoutIndex) => (
                          <div key={workoutIndex} className="bg-white p-3 rounded border border-slate-200 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-800">{workout.title}</p>
                              <p className="text-xs text-slate-600">
                                {workout.day} • {workout.duration} • {workout.workoutType === 'run' ? '🏃 Run' : `${workout.exercises?.length || 0} exercises`}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => editWorkoutInWeek(weekIndex, workoutIndex)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteWorkoutFromWeek(weekIndex, workoutIndex)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500 italic">No workouts added yet</p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setCurrentWeek(weekIndex);
                        setCurrentWorkout(null);
                        setWorkoutFormInProgram({
                          id: '',
                          title: '',
                          day: '',
                          duration: '',
                          difficulty: 'Beginner',
                          equipment: [],
                          exercises: []
                        });
                      }}
                      className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
                    >
                      + Add Workout to Week {week.week}
                    </button>
                  </div>
                ))}

                {/* Calendar View */}
                {programViewMode === 'calendar' && (
                  <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
                    {/* Calendar Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
                      <h3 className="text-xl font-bold">{programForm.title || 'New Program'}</h3>
                      <p className="text-sm opacity-90">{programForm.duration} • {programForm.level}</p>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-px bg-slate-200">
                      {/* Day Headers */}
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <div key={day} className="bg-slate-100 p-2 text-center text-xs font-semibold text-slate-600">
                          {day}
                        </div>
                      ))}

                      {/* Week Rows */}
                      {programForm.weeks.map((week, weekIndex) => (
                        <React.Fragment key={weekIndex}>
                          {/* Week Label */}
                          <div className="col-span-7 bg-slate-50 p-3 border-t-2 border-slate-300">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <h4 className="font-semibold text-slate-800">
                                  Week {week.week}
                                  <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                                    {week.workouts?.length || 0} workout{week.workouts?.length !== 1 ? 's' : ''}
                                  </span>
                                </h4>
                                <input
                                  type="text"
                                  placeholder="Week Title"
                                  value={week.title}
                                  onChange={(e) => {
                                    const updatedWeeks = [...programForm.weeks];
                                    updatedWeeks[weekIndex].title = e.target.value;
                                    setProgramForm({...programForm, weeks: updatedWeeks});
                                  }}
                                  className="border border-slate-300 rounded px-2 py-1 text-xs"
                                />
                              </div>
                              {week.workouts && week.workouts.length > 0 && (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200"
                                    onClick={() => {
                                      const targetWeeks = programForm.weeks
                                        .map((w, i) => i)
                                        .filter(i => i !== weekIndex);
                                      if (window.confirm(`Duplicate Week ${week.week} to all other weeks?`)) {
                                        targetWeeks.forEach(targetIndex => {
                                          duplicateWeek(weekIndex, targetIndex);
                                        });
                                      }
                                    }}
                                  >
                                    📋 Duplicate to All
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Days of Week */}
                          {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                            const workout = week.workouts?.[dayIndex];
                            return (
                              <div
                                key={dayIndex}
                                className="bg-white p-3 min-h-[120px] hover:bg-slate-50 transition-colors"
                              >
                                {workout ? (
                                  <div className="space-y-2">
                                    <div className="font-medium text-sm text-slate-800 line-clamp-1">
                                      {workout.title}
                                    </div>
                                    <div className="text-xs text-slate-600 space-y-1">
                                      <div>{workout.duration}</div>
                                      <div>{workout.exercises?.length || 0} exercises</div>
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => editWorkoutInWeek(weekIndex, dayIndex)}
                                        className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => deleteWorkoutFromWeek(weekIndex, dayIndex)}
                                        className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCurrentWeek(weekIndex);
                                      setCurrentWorkout(dayIndex);
                                      setWorkoutFormInProgram({
                                        id: '',
                                        title: '',
                                        day: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex],
                                        duration: '',
                                        difficulty: 'Beginner',
                                        equipment: [],
                                        exercises: []
                                      });
                                    }}
                                    className="w-full h-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded border-2 border-dashed border-slate-200 hover:border-slate-300 transition-all"
                                  >
                                    <span className="text-2xl">+</span>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Workout Builder (shown when adding/editing a workout) */}
              {(currentWeek !== null && workoutFormInProgram.title !== undefined) && (
                <div className="border-2 border-orange-300 rounded-lg p-6 bg-orange-50">
                  <h4 className="text-lg font-semibold text-slate-800 mb-4">
                    {currentWorkout !== null ? 'Edit Workout' : `Add Workout to Week ${currentWeek + 1}`}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Workout Title"
                      value={workoutFormInProgram.title}
                      onChange={(e) => setWorkoutFormInProgram({...workoutFormInProgram, title: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="text"
                      placeholder="Day (e.g., Monday)"
                      value={workoutFormInProgram.day}
                      onChange={(e) => setWorkoutFormInProgram({...workoutFormInProgram, day: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="text"
                      placeholder="Duration (e.g., 45 mins)"
                      value={workoutFormInProgram.duration}
                      onChange={(e) => setWorkoutFormInProgram({...workoutFormInProgram, duration: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    />
                    <select
                      value={workoutFormInProgram.difficulty}
                      onChange={(e) => setWorkoutFormInProgram({...workoutFormInProgram, difficulty: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Equipment (comma separated)"
                      value={Array.isArray(workoutFormInProgram.equipment) ? workoutFormInProgram.equipment.join(', ') : workoutFormInProgram.equipment}
                      onChange={(e) => setWorkoutFormInProgram({...workoutFormInProgram, equipment: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2 md:col-span-2"
                    />
                  </div>

                  {/* Exercise List */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-slate-700 mb-2">Exercises ({workoutFormInProgram.exercises.length})</h5>
                    <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                      {workoutFormInProgram.exercises.map((exercise, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-slate-200 text-sm">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              {/* Order Controls */}
                              <div className="flex flex-col gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => moveExerciseUp(idx)}
                                  disabled={idx === 0}
                                  className={`text-xs ${idx === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:text-slate-800'}`}
                                  title="Move up"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveExerciseDown(idx)}
                                  disabled={idx === workoutFormInProgram.exercises.length - 1}
                                  className={`text-xs ${idx === workoutFormInProgram.exercises.length - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:text-slate-800'}`}
                                  title="Move down"
                                >
                                  ▼
                                </button>
                              </div>
                              
                              {/* Exercise Info */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-slate-500 font-medium">#{idx + 1}</span>
                                {exercise.supersetGroup && (
                                  <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-1 rounded">
                                    {exercise.supersetGroup}
                                  </span>
                                )}
                                <span className="font-medium text-slate-800">{exercise.name}</span>
                                {exercise.tempo && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Tempo: {exercise.tempo}</span>}
                                {exercise.videoUrl && (
                                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">📹 Video</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 ml-2">
                              <button
                                type="button"
                                onClick={() => editExerciseInWorkout(exercise)}
                                className="text-blue-600 hover:text-blue-800 text-xs font-medium whitespace-nowrap"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => removeExerciseFromWorkout(exercise.id)}
                                className="text-red-600 hover:text-red-800 text-xs font-medium whitespace-nowrap"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {exercise.sets.map((set, setIdx) => (
                              <div key={setIdx} className="text-xs text-slate-600 flex items-center gap-2">
                                <span className="font-medium">Set {set.setNumber}:</span>
                                <span>{set.isTimed ? `${set.reps} sec` : `${set.reps} reps`}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] ${
                                  set.type === 'warmup' ? 'bg-blue-100 text-blue-700' :
                                  set.type === 'dropset' ? 'bg-purple-100 text-purple-700' :
                                  set.type === 'failure' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {set.type === 'warmup' ? 'Warm-up' :
                                   set.type === 'dropset' ? 'Drop Set' :
                                   set.type === 'failure' ? 'To Failure' :
                                   'Normal'}
                                </span>
                              </div>
                            ))}
                            <div className="text-xs text-slate-500 mt-1">Rest: {exercise.restTime}</div>
                            {exercise.videoUrl && (
                              <div className="text-xs text-blue-600 mt-1">
                                <a href={exercise.videoUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                  🎥 View tutorial
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Exercise Form */}
                    <div className="bg-white p-4 rounded border border-slate-300">
                      <div className="flex items-center justify-between mb-3">
                        <h6 className="text-sm font-semibold text-slate-700">Add Exercise</h6>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShowExerciseLibraryModal(true)}
                            className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded border border-green-300 hover:bg-green-200"
                          >
                            📚 Exercise Library ({exerciseLibrary.length})
                          </button>
                          <div className="text-xs text-slate-500 bg-blue-50 px-3 py-1 rounded border border-blue-200">
                            💡 Supersets, Tempo, Timed sets
                          </div>
                        </div>
                      </div>

                      {/* Exercise Name with Autocomplete */}
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-2">
                          Exercise Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Start typing exercise name..."
                            value={exerciseSearchQuery || exerciseForm.name}
                            onChange={(e) => {
                              const query = e.target.value;
                              setExerciseSearchQuery(query);
                              setExerciseForm({...exerciseForm, name: query});
                              setShowExerciseSuggestions(query.length > 0 && exerciseLibrary.length > 0);
                            }}
                            onFocus={() => setShowExerciseSuggestions(exerciseSearchQuery.length > 0 && exerciseLibrary.length > 0)}
                            onBlur={() => setTimeout(() => setShowExerciseSuggestions(false), 200)}
                            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
                          />
                          
                          {/* Autocomplete Suggestions */}
                          {showExerciseSuggestions && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {exerciseLibrary
                                .filter(ex => ex.name.toLowerCase().includes(exerciseSearchQuery.toLowerCase()))
                                .slice(0, 5)
                                .map(ex => (
                                  <button
                                    key={ex.id}
                                    type="button"
                                    onClick={() => {
                                      loadExerciseFromLibrary(ex);
                                      setExerciseSearchQuery(ex.name);
                                      setShowExerciseSuggestions(false);
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                                  >
                                    <div className="font-medium text-sm text-slate-800">{ex.name}</div>
                                    <div className="text-xs text-slate-600 mt-1">
                                      {Array.isArray(ex.muscleGroups) ? ex.muscleGroups.join(', ') : (ex.muscleGroups || '')}
                                      {ex.videoUrl && ' • 📹 Video'}
                                    </div>
                                  </button>
                                ))}
                              {exerciseLibrary.filter(ex => ex.name.toLowerCase().includes(exerciseSearchQuery.toLowerCase())).length === 0 && (
                                <div className="px-4 py-3 text-sm text-slate-500 italic">
                                  No exercises found. This will create a new exercise.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          💡 Start typing to see suggestions from your library
                        </p>
                      </div>
                      
                      {/* Exercise Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">Superset (optional)</label>
                          <input
                            type="text"
                            placeholder="A1, A2, B1, etc."
                            value={exerciseForm.supersetGroup}
                            onChange={(e) => setExerciseForm({...exerciseForm, supersetGroup: e.target.value.toUpperCase()})}
                            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
                            maxLength="2"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">Tempo (optional)</label>
                          <input
                            type="text"
                            placeholder="e.g., 3-1-1-0"
                            value={exerciseForm.tempo}
                            onChange={(e) => setExerciseForm({...exerciseForm, tempo: e.target.value})}
                            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">Rest Time</label>
                          <input
                            type="text"
                            placeholder="e.g., 60s, 90s"
                            value={exerciseForm.restTime}
                            onChange={(e) => setExerciseForm({...exerciseForm, restTime: e.target.value})}
                            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-1">Muscle Groups</label>
                        <input
                          type="text"
                          placeholder="e.g., Glutes, Hamstrings, Quads"
                          value={Array.isArray(exerciseForm.muscleGroups) ? exerciseForm.muscleGroups.join(', ') : exerciseForm.muscleGroups}
                          onChange={(e) => setExerciseForm({...exerciseForm, muscleGroups: e.target.value})}
                          className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
                        />
                      </div>

                      {/* Set Configuration */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-semibold text-slate-700">Set Configuration</label>
                          <button
                            type="button"
                            onClick={() => {
                              setExerciseForm({
                                ...exerciseForm,
                                sets: [...exerciseForm.sets, { 
                                  setNumber: exerciseForm.sets.length + 1, 
                                  reps: '10-12', 
                                  type: 'normal',
                                  isTimed: false
                                }]
                              });
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                          >
                            + Add Set
                          </button>
                        </div>
                        
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {exerciseForm.sets.map((set, setIdx) => (
                            <div key={setIdx} className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                              <span className="text-xs font-medium text-slate-600 w-12">Set {set.setNumber}</span>
                              <input
                                type="text"
                                placeholder={set.isTimed ? "Seconds" : "Reps"}
                                value={set.reps}
                                onChange={(e) => {
                                  const updatedSets = [...exerciseForm.sets];
                                  updatedSets[setIdx].reps = e.target.value;
                                  setExerciseForm({...exerciseForm, sets: updatedSets});
                                }}
                                className="border border-slate-300 rounded px-2 py-1 text-xs flex-1"
                              />
                              <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={set.isTimed || false}
                                  onChange={(e) => {
                                    const updatedSets = [...exerciseForm.sets];
                                    updatedSets[setIdx].isTimed = e.target.checked;
                                    setExerciseForm({...exerciseForm, sets: updatedSets});
                                  }}
                                  className="w-3 h-3"
                                />
                                Timed
                              </label>
                              <select
                                value={set.type}
                                onChange={(e) => {
                                  const updatedSets = [...exerciseForm.sets];
                                  updatedSets[setIdx].type = e.target.value;
                                  setExerciseForm({...exerciseForm, sets: updatedSets});
                                }}
                                className="border border-slate-300 rounded px-2 py-1 text-xs"
                              >
                                <option value="warmup">Warm-up</option>
                                <option value="normal">Normal</option>
                                <option value="dropset">Drop Set</option>
                                <option value="failure">To Failure</option>
                              </select>
                              {exerciseForm.sets.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedSets = exerciseForm.sets.filter((_, i) => i !== setIdx);
                                    // Renumber sets
                                    updatedSets.forEach((s, i) => s.setNumber = i + 1);
                                    setExerciseForm({...exerciseForm, sets: updatedSets});
                                  }}
                                  className="text-red-600 hover:text-red-800 text-xs px-1"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Rest Time and Options */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Rest (e.g., 60s)"
                          value={exerciseForm.restTime}
                          onChange={(e) => setExerciseForm({...exerciseForm, restTime: e.target.value})}
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                        <label className="flex items-center gap-2 text-sm border border-slate-300 rounded px-3 py-2">
                          <input
                            type="checkbox"
                            checked={exerciseForm.needsWeight}
                            onChange={(e) => setExerciseForm({...exerciseForm, needsWeight: e.target.checked})}
                          />
                          Needs Weight
                        </label>
                      </div>

                      {/* Instructions */}
                      <textarea
                        placeholder="Instructions (form cues, tips, etc.)"
                        value={exerciseForm.instructions}
                        onChange={(e) => setExerciseForm({...exerciseForm, instructions: e.target.value})}
                        className="border border-slate-300 rounded px-3 py-2 w-full h-16 text-sm"
                        rows="2"
                      />
                      
                      {/* Video Guide URL */}
                      <div className="mt-3">
                        <label className="text-xs font-semibold text-slate-700 mb-1 block">Video Guide (Optional)</label>
                        <input
                          type="url"
                          placeholder="YouTube, Vimeo, or direct video URL"
                          value={exerciseForm.videoUrl}
                          onChange={(e) => setExerciseForm({...exerciseForm, videoUrl: e.target.value})}
                          className="border border-slate-300 rounded px-3 py-2 w-full text-sm"
                        />
                        <p className="text-xs text-slate-500 mt-1">💡 Add a video tutorial link for members to reference</p>
                      </div>
                      
                      <div className="space-y-2 mt-3">
                        {/* Superset Linking Option */}
                        {lastAddedExerciseId && workoutFormInProgram.exercises.length > 0 && (
                          <div className="bg-orange-50 border border-orange-200 rounded p-3">
                            <p className="text-xs font-semibold text-slate-700 mb-2">
                              💡 Link to previous exercise?
                            </p>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => addExerciseToWorkout(true)}
                                disabled={!exerciseForm.name}
                                className="flex-1 bg-orange-600 text-white px-3 py-2 rounded text-sm hover:bg-orange-700 disabled:bg-gray-400"
                              >
                                🔗 Add as Superset
                              </button>
                              <button
                                type="button"
                                onClick={() => addExerciseToWorkout(false)}
                                disabled={!exerciseForm.name}
                                className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                              >
                                ✓ Add Separate
                              </button>
                            </div>
                            <p className="text-xs text-slate-600 mt-2">
                              Superset: Exercises performed back-to-back with no rest
                            </p>
                          </div>
                        )}
                        
                        {/* Regular Add Buttons (when no previous exercise) */}
                        {(!lastAddedExerciseId || workoutFormInProgram.exercises.length === 0) && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => addExerciseToWorkout(false)}
                              disabled={!exerciseForm.name}
                              className="flex-1 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                            >
                              ✓ Add Exercise to Workout
                            </button>
                            <button
                              type="button"
                              onClick={saveExerciseToLibrary}
                              disabled={!exerciseForm.name}
                              className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 disabled:bg-gray-400"
                              title="Save this exercise to library for reuse"
                            >
                              💾 Save to Library
                            </button>
                          </div>
                        )}
                        
                        {/* Save to Library (always available) */}
                        {lastAddedExerciseId && workoutFormInProgram.exercises.length > 0 && (
                          <button
                            type="button"
                            onClick={saveExerciseToLibrary}
                            disabled={!exerciseForm.name}
                            className="w-full bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 disabled:bg-gray-400"
                            title="Save this exercise to library for reuse"
                          >
                            💾 Save to Library
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={addWorkoutToWeek}
                      disabled={!workoutFormInProgram.title || workoutFormInProgram.exercises.length === 0}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {currentWorkout !== null ? 'Update Workout' : 'Add Workout'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentWeek(null);
                        setCurrentWorkout(null);
                        setWorkoutFormInProgram({
                          id: '',
                          title: '',
                          day: '',
                          duration: '',
                          difficulty: 'Beginner',
                          equipment: [],
                          exercises: []
                        });
                      }}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Submit Program */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-lg border-2 border-orange-300">
                <div className="mb-4">
                  <h4 className="text-lg font-bold text-orange-800 mb-2">⚠️ Final Step: Save Your Program</h4>
                  <p className="text-sm text-slate-700">
                    You've added workouts to your weeks. Now click the button below to save everything to the database.
                  </p>
                  <p className="text-xs text-slate-600 mt-2">
                    Total workouts: <span className="font-bold">{programForm.weeks.reduce((sum, week) => sum + (week.workouts?.length || 0), 0)}</span>
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 font-bold text-lg shadow-lg"
                  >
                    {editingItem ? '💾 Update Program' : '✨ Create Program'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProgramModal(false);
                      resetProgramForm();
                    }}
                    className="bg-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exercise Library Modal */}
      {showExerciseLibraryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-bold text-slate-800">📚 Exercise Library</h3>
              <p className="text-sm text-slate-600 mt-1">
                Manage your saved exercises. Click an exercise to load it into the form.
              </p>
            </div>
            
            <div className="p-6">
              {exerciseLibrary.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500 mb-2">No exercises saved yet</p>
                  <p className="text-sm text-slate-400">
                    Create an exercise and click "💾 Save to Library" to add it here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {exerciseLibrary.map((exercise) => (
                    <div key={exercise.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800 mb-1">{exercise.name}</h4>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(Array.isArray(exercise.muscleGroups) ? exercise.muscleGroups : []).map((muscle, idx) => (
                              <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {muscle}
                              </span>
                            ))}
                          </div>
                          <div className="text-xs text-slate-600 space-y-1">
                            {exercise.defaultRestTime && (
                              <p>Rest: {exercise.defaultRestTime}</p>
                            )}
                            {exercise.defaultTempo && (
                              <p>Tempo: {exercise.defaultTempo}</p>
                            )}
                            {exercise.videoUrl && (
                              <p className="text-blue-600">📹 Video tutorial included</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            type="button"
                            onClick={() => {
                              loadExerciseFromLibrary(exercise);
                              setShowExerciseLibraryModal(false);
                            }}
                            className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                          >
                            Use
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteExerciseFromLibrary(exercise.id)}
                            className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-slate-50">
              <button
                type="button"
                onClick={() => setShowExerciseLibraryModal(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
